import { cfApiClient } from '@/api/cf-worker-client';
import type { Product, CreateProduct, ProductAttribute } from '@/types';

interface SearchProductItem {
  productId: string;
  productCode: string;
  name: string;
  title: string;
  description: string;
  price: number;
  categoryName: string;
  thumbnailUrl: string;
  sku?: string | null;
  images: { url: string; filename?: string }[];
  tags: { name: string; value: string }[];
  rating: number;
  reviewCount: number;
  status: 'published' | 'draft';
  attributes: ProductAttribute[];
}

interface SearchApiResponse {
  success: boolean;
  data: {
    query: string;
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    content: SearchProductItem[];
    facets: {
      categories: { name: string; count: number }[];
      priceRanges: { min: number; max: number; count: number }[];
      tags: { name: string; values: { value: string; count: number }[] }[];
      attributes: { name: string; count: number }[];
    };
  };
}

// API response for creating a product (camelCase)
interface CreateProductResponse {
  success: boolean;
  data: {
    productId: string;
    productCode: string;
    name: string;
    title: string;
    description?: string | null;
    categoryId: string;
    status: 'draft' | 'published';
    attributes: string[];
    images?: { id?: string; cid?: string; filename?: string; url: string; sortOrder?: number }[];
    variants?: unknown[];
    tags?: unknown[];
    sections?: unknown[];
    createdAt: string;
    updatedAt: string;
  };
}

// API response for getting/updating a product (camelCase in API, mapped to snake_case)
interface ProductApiResponse {
  success: boolean;
  data: {
    productId: string;
    productCode: string;
    name: string;
    title: string;
    description?: string | null;
    categoryId: string;
    price: number;
    cost?: number | null;
    sku?: string | null;
    status: 'draft' | 'published';
    attributes: string[];
    thumbnailUrl?: string | null;
    categoryName: string;
    images: { id?: string; cid?: string; filename?: string; url: string; sortOrder?: number }[];
    variants?: unknown[];
    tags?: unknown[];
    sections?: unknown[];
    createdAt: string;
    updatedAt: string;
  };
}

// Helper to map API response (camelCase) to Product type (camelCase)
const mapApiProductToProduct = (apiProduct: any): Product => {
  return {
    id: String(apiProduct.productId || apiProduct.id || ''),
    productCode: apiProduct.productCode || '',
    name: apiProduct.name || '',
    title: apiProduct.title || apiProduct.name || '',
    description: apiProduct.description || null,
    categoryId: String(apiProduct.categoryId || ''),
    price: apiProduct.price || 0,
    cost: apiProduct.cost || null,
    sku: apiProduct.sku || null,
    status: apiProduct.status || 'draft',
    attributes: apiProduct.attributes || [],
    thumbnailUrl: apiProduct.thumbnailUrl || null,
    categoryName: apiProduct.categoryName || '',
    images: (apiProduct.images || []).map((img: any, index: number) => ({
      id: img.id,
      cid: img.cid,
      filename: img.filename,
      url: img.url,
      sortOrder: img.sortOrder ?? index,
      isThumbnail: !!img.isThumbnail,
    })),
    variants: apiProduct.variants || [],
    tags: apiProduct.tags || [],
    sections: apiProduct.sections || [],
    createdAt: apiProduct.createdAt || new Date().toISOString(),
    updatedAt: apiProduct.updatedAt || new Date().toISOString(),
  };
};

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
  };
}

export interface GetProductsParams {
  searchQuery?: string;
  page?: number;
  size?: number;
  category?: string;
  status?: string;
  sort?: 'newest' | 'popularity' | 'price_asc' | 'price_desc';
  attributes?: string[];
  skipCache?: boolean;
}

const mapSearchItemToProduct = (item: SearchProductItem): Product => {
  return {
    id: item.productId,
    productCode: item.productCode,
    name: item.name,
    title: item.title,
    description: item.description,
    categoryId: '', // Will need to be fetched separately or added to search response
    price: item.price,
    status: (item.status === 'published' || item.status === 'draft') ? item.status : 'draft',
    attributes: item.attributes || [],
    sku: item.sku || null,
    thumbnailUrl: item.thumbnailUrl || null,
    categoryName: item.categoryName,
    images: item.images.map((img, index) => ({
      url: img.url,
      filename: img.filename,
      sortOrder: index,
    })),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

export const productsService = {
  getProducts: async (params: GetProductsParams = {}): Promise<ProductsResponse> => {
    const {
      searchQuery = '',
      page = 1,
      size = 10,
      category,
      status,
      sort,
      attributes,
      skipCache,
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('q', searchQuery);
    queryParams.append('page', String(page));
    queryParams.append('size', String(size));
    if (category) queryParams.append('category', category);
    if (status) queryParams.append('status', status);
    if (sort) queryParams.append('sort', sort);
    if (attributes && attributes.length > 0) {
      attributes.forEach((attr: string) => queryParams.append('attributes', attr));
    }
    if (skipCache) queryParams.append('skipCache', 'true');

    const response = await cfApiClient.get<SearchApiResponse>(`/search?${queryParams.toString()}`);
    const products = response.data?.content?.map(mapSearchItemToProduct) || [];

    return {
      products,
      pagination: {
        page: response.data?.page || page,
        size: response.data?.size || size,
        totalElements: response.data?.totalElements || 0,
        totalPages: response.data?.totalPages || 1,
      },
    };
  },

  getProduct: async (productCode: string): Promise<Product> => {
    const response = await cfApiClient.get<ProductApiResponse>(`/products/${productCode}`);
    return mapApiProductToProduct(response.data);
  },

  createProduct: async (product: CreateProduct): Promise<Product> => {
    const response = await cfApiClient.post<CreateProductResponse>('/products', product);
    return mapApiProductToProduct(response.data);
  },

  updateProduct: async (productCode: string, product: Partial<Product>): Promise<Product> => {
    const response = await cfApiClient.put<ProductApiResponse>(`/products/${productCode}`, product);
    return mapApiProductToProduct(response.data);
  },

  uploadProductImages: async (productCode: string, files: File[]): Promise<{ id: string; cid: string; filename: string; url: string }[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    const response = await cfApiClient.upload<{
      success: boolean;
      data: {
        productCode: string;
        images: { id: string; cid: string; filename: string; url: string; size: string; uploadedAt: string }[];
        message: string;
      }
    }>(`/products/${productCode}/images`, formData);

    return response.data.images.map(img => ({
      id: img.id,
      cid: img.cid,
      filename: img.filename,
      url: img.url,
    }));
  },

  deleteProduct: (productCode: string): Promise<void> => {
    return cfApiClient.delete<void>(`/products/${productCode}`);
  },
};
